import { observer } from "mobx-react";
import React, { useState } from 'react';
import { Button, Col, Form, InputGroup, InputGroupProps, Row } from "react-bootstrap";

export type QuerySearchButtonSetQuery = (param: string) => void;
interface IQuerySearchButtonProps {
    inputGroupProps?: InputGroupProps
    setQuery: QuerySearchButtonSetQuery;
    placeholderText: string;
    clearButtonText?: string;
    /**
     * If undefined, no live search occurs, if numeric setQuery will be called as soon as this amount in milliseconds has elapsed without a new input.  Input within this delay debounces and resets this delay so only one call
     * to setQuery will happen withing this elapsed milliseconds.  Leave undefined to not use liveSearch.
     */
    liveSearchDelay?: number;
    omitWrapperMarkup?: boolean;
}

const QuerySearchButton = observer(({ setQuery, placeholderText, clearButtonText = "Clear Filter", liveSearchDelay, omitWrapperMarkup = false, inputGroupProps = undefined }: IQuerySearchButtonProps) => {
    const bounce = React.useRef<(() => void) | undefined>(undefined);
    const [keyword, setKeyword] = useState<string>("");
    const [doLiveSearch, setDoLiveSearch] = useState(false);

    const reset = () => {
        setKeyword("");
        setQuery("")
    }

    //keyword could still be undefined when liveSearchSetQuery is called because state was updated by onChange and react hasn't
    //finished the rendering loop yet, so it'll call this FC again with the keyword on the 2nd pass, so instead of reading keyword here, we'll setState to execute the live
    //search so the useEffect will handle actually calling setQuery
    const liveSearchSetQuery = React.useCallback(() => {
        if (!bounce.current) {
            bounce.current = _.debounce(() => {
                bounce.current = undefined;
                setDoLiveSearch(true);
            }, liveSearchDelay !== undefined ? liveSearchDelay : 500, { leading: false, trailing: true });
            bounce.current();
        } else
            bounce.current();
    }, [liveSearchDelay, setDoLiveSearch]);

    React.useEffect(() => {
        if (doLiveSearch) {
            setQuery(keyword);
            setDoLiveSearch(false);
        }
    }, [doLiveSearch, setQuery, keyword])

    const onChangeHandler: React.ChangeEventHandler<HTMLInputElement> = React.useCallback((e) => {
        setKeyword(e.target.value);
        if (liveSearchDelay !== undefined)
            liveSearchSetQuery();
    }, [liveSearchDelay, liveSearchSetQuery]);

    const handleKeypress = (e: React.KeyboardEvent<any>) => {
        //it triggers by pressing the enter key
        if (e.key === "Enter" && liveSearchDelay == null) {
            e.preventDefault();
            setQuery(keyword);
        }
    };

    if (!omitWrapperMarkup) {
        return (
            <Row className="align-items-center">
                <Col>
                    <InputGroup {...inputGroupProps}>
                        <span aria-label="search-icon" className="searchIcon cursor-pointer" onClick={() => setQuery(keyword)} />
                        <Form.Control
                            name="keyword"
                            value={keyword}
                            onKeyUp={handleKeypress}
                            onChange={onChangeHandler}
                            className="rounded-search"
                            placeholder={placeholderText} />
                    </InputGroup>
                </Col>
                <Col>
                    <Button variant="light" className='cancel' onClick={() => reset()}>{clearButtonText}</Button>
                </Col>
            </Row>
        )
    }

    return (<InputGroup {...inputGroupProps}>
        <span role="search" aria-label="search-icon" className="searchIcon cursor-pointer" onClick={() => setQuery(keyword)} />
        <Form.Control
            name="keyword"
            value={keyword}
            onKeyUp={handleKeypress}
            onChange={onChangeHandler}
            className="rounded-search"
            placeholder={placeholderText} />
    </InputGroup>);


});

export default QuerySearchButton;
